import { createApi } from '@reduxjs/toolkit/query/react';
import { createExtendedTokenBaseQuery } from './baseQuery';

// ---------------------------------------------------------------------------
// Enumerations (see notifications_api.md)
// ---------------------------------------------------------------------------

export type NotificationType =
  | 'task_assignment'
  | 'task_assignment_sso'
  | 'task_reminder'
  | 'task_escalation'
  | 'task_completion'
  | 'workflow_approval'
  | 'workflow_rejection'
  | 'workflow_completion'
  | 'workflow_cancellation'
  | 'comment_added'
  | 'sla_breach'
  | 'delegation'
  | 'schedule_activity_generated'
  | 'schedule_activity_due'
  | 'schedule_activity_overdue'
  | 'one_off_activity_created'
  | 'document_download'
  | 'report_download'
  | 'report_generation_failed'
  | 'audit_assignment'
  | 'audit_reverification'
  | 'custom';

export type NotificationChannel = 'in_app' | 'email' | 'sms' | 'push';

export type NotificationFrequency = 'immediate' | 'hourly' | 'daily' | 'weekly';

/** Computed aggregate — always read-only. Never sent as input. */
export type AggregateState = 'enabled' | 'disabled' | 'partial';

export interface ChannelFlags {
  in_app: boolean;
  email: boolean;
  sms: boolean;
  push: boolean;
}

/** Partial version used for channel update / global toggle requests. */
export type ChannelFlagsPartial = Partial<ChannelFlags>;

// ---------------------------------------------------------------------------
// Resource interfaces
// ---------------------------------------------------------------------------

export interface NotificationPreference {
  id: string;
  user: string;
  notification_type: NotificationType;
  channels: ChannelFlags;
  frequency: NotificationFrequency;
  enabled: boolean;
}

export interface Notification {
  id: string;
  user: string;
  title: string;
  message: string;
  notification_type: NotificationType;
  created_at: string;
  read: boolean;
  related_entity_type?: string | null;
  related_entity_id?: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface NotificationPaginatedResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Notification[];
}

export interface NotificationPreferencePage {
  count: number;
  results: NotificationPreference[];
}

export interface GlobalStatusResponse {
  /** Aggregate of the preference.enabled field across all types. */
  status: AggregateState;
  total: number;
  enabled_count: number;
  /** Per-channel aggregate — whether each channel is on across all types. */
  channels: {
    in_app: AggregateState;
    email: AggregateState;
    sms: AggregateState;
    push: AggregateState;
  };
}

// ---------------------------------------------------------------------------
// API slice
// ---------------------------------------------------------------------------

const GLOBAL_STATUS_ID = 'GLOBAL_STATUS';

export const notificationApi = createApi({
  reducerPath: 'notificationApi',
  baseQuery: createExtendedTokenBaseQuery(''),
  tagTypes: ['Notification', 'NotificationPreference'],
  endpoints: (builder) => ({
    getNotifications: builder.query<
      NotificationPaginatedResponse,
      { page?: number; limit?: number; unread_only?: boolean; notification_type?: string }
    >({
      query: (params = { page: 1, limit: 10, unread_only: false }) => {
        const queryParams = new URLSearchParams();
        if (params.page) queryParams.append('page', params.page.toString());
        if (params.limit) queryParams.append('limit', params.limit.toString());
        if (params.notification_type) queryParams.append('notification_type', params.notification_type);

        return {
          url: params.unread_only ? 'notifications/api/unread/' : 'notifications/api/',
          params: queryParams,
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({ id }) => ({ type: 'Notification' as const, id })),
              { type: 'Notification' as const, id: 'LIST' },
            ]
          : [{ type: 'Notification' as const, id: 'LIST' }],
    }),

    getNotificationCount: builder.query<
      {
        count: number;
        type_breakdown: Record<string, number>;
      },
      { notification_type?: string }
    >({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.notification_type) {
          queryParams.append('notification_type', params.notification_type);
        }

        return {
          url: 'notifications/api/unread/count/',
          params: queryParams,
        };
      },
      providesTags: [{ type: 'Notification' as const, id: 'COUNT' }],
    }),

    markNotificationAsRead: builder.mutation<void, string>({
      query: (id) => ({
        url: `notifications/api/${id}/read/`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Notification' as const, id },
        { type: 'Notification' as const, id: 'COUNT' },
      ],
    }),

    markAllNotificationsAsRead: builder.mutation<{ count: number }, void>({
      query: () => ({
        url: 'notifications/api/read-all/',
        method: 'PUT',
      }),
      invalidatesTags: [
        { type: 'Notification' as const, id: 'LIST' },
        { type: 'Notification' as const, id: 'COUNT' },
      ],
    }),

    // ---------------------------------------------------------------------
    // Preferences
    // ---------------------------------------------------------------------

    /**
     * List all preferences for the current user. The backend auto-creates a
     * record for every notification type on first access.
     */
    getNotificationPreferences: builder.query<
      NotificationPreferencePage,
      { page?: number; limit?: number } | void
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params?.page) queryParams.append('page', params.page.toString());
        if (params?.limit) queryParams.append('limit', params.limit.toString());
        return {
          url: 'notifications/api/preferences/',
          params: queryParams,
        };
      },
      // The endpoint may return a bare array or a paginated { count, results }
      // envelope depending on backend config — normalise to { count, results }.
      transformResponse: (
        response: NotificationPreference[] | { count?: number; results?: NotificationPreference[] }
      ): NotificationPreferencePage => {
        if (Array.isArray(response)) {
          return { count: response.length, results: response };
        }
        const results = Array.isArray(response?.results) ? response.results : [];
        return { count: response?.count ?? results.length, results };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({ id }) => ({ type: 'NotificationPreference' as const, id })),
              { type: 'NotificationPreference' as const, id: 'LIST' },
            ]
          : [{ type: 'NotificationPreference' as const, id: 'LIST' }],
    }),

    /**
     * Partial update of a single preference — frequency and/or enabled and/or
     * the full channels object. Send only the fields you want to change.
     */
    updateNotificationPreference: builder.mutation<
      NotificationPreference,
      { id: string } & Partial<Pick<NotificationPreference, 'channels' | 'frequency' | 'enabled'>>
    >({
      query: ({ id, ...body }) => ({
        url: `notifications/api/preferences/${id}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'NotificationPreference' as const, id: arg.id },
        { type: 'NotificationPreference' as const, id: 'LIST' },
        { type: 'NotificationPreference' as const, id: GLOBAL_STATUS_ID },
      ],
    }),

    /**
     * Merge-patch channel flags for a single preference. Channels that are
     * omitted from the body are left untouched.
     */
    updatePreferenceChannels: builder.mutation<
      NotificationPreference,
      { id: string; channels: ChannelFlagsPartial }
    >({
      query: ({ id, channels }) => ({
        url: `notifications/api/preferences/${id}/channels/`,
        method: 'PATCH',
        body: channels,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'NotificationPreference' as const, id: arg.id },
        { type: 'NotificationPreference' as const, id: 'LIST' },
        { type: 'NotificationPreference' as const, id: GLOBAL_STATUS_ID },
      ],
    }),

    /**
     * Aggregate channel and enabled state across all preference types — used to
     * drive the global master/channel switches (enabled | disabled | partial).
     */
    getGlobalStatus: builder.query<GlobalStatusResponse, void>({
      query: () => 'notifications/api/preferences/global-status/',
      providesTags: [{ type: 'NotificationPreference' as const, id: GLOBAL_STATUS_ID }],
    }),

    /**
     * Bulk-apply channel flags across every notification type (global channel
     * toggle). Only the channels included in the body are changed; this does
     * NOT touch the per-type `enabled` field.
     */
    setGlobalChannels: builder.mutation<void, ChannelFlagsPartial>({
      query: (channels) => ({
        url: 'notifications/api/preferences/global-status/',
        method: 'PUT',
        body: channels,
      }),
      invalidatesTags: [
        { type: 'NotificationPreference' as const, id: 'LIST' },
        { type: 'NotificationPreference' as const, id: GLOBAL_STATUS_ID },
      ],
    }),
  }),
});

export const {
  useGetNotificationsQuery,
  useGetNotificationCountQuery,
  useMarkNotificationAsReadMutation,
  useMarkAllNotificationsAsReadMutation,
  useGetNotificationPreferencesQuery,
  useUpdateNotificationPreferenceMutation,
  useUpdatePreferenceChannelsMutation,
  useGetGlobalStatusQuery,
  useSetGlobalChannelsMutation,
} = notificationApi;
